import { jourArrivage } from '../utils/dates';
import useBreakpoint from '../utils/useBreakpoint';
import { Bouton, Capsule } from './ui';
import {
  ACCENT, AMBRE, AMBRE_FG, NEUTRE, OMBRE_CARTE, RAYON_CARTE, REMPLISSAGE, SURFACE, TEXTE, TEXTE_MUET, TEXTE_SECONDAIRE, VERT,
} from '../theme';

/**
 * État réel d'un arrivage — reflète MeasurementArrivageDTO. Ce sont des faits renvoyés par le
 * serveur : `normesDefinies`, `sessionId`, `quotaAtteint`. Rien n'est recalculé côté écran.
 */
function etatArrivage(a) {
  if (!a.normesDefinies) {
    return { libelle: 'Normes à définir', ton: 'orange', jauge: AMBRE, bloquant: true };
  }
  if (!a.sessionId) {
    return { libelle: 'À mesurer', ton: 'neutre', jauge: NEUTRE, bloquant: false };
  }
  if (a.quotaAtteint) {
    return { libelle: 'Quota atteint', ton: 'vert', jauge: VERT, bloquant: false };
  }
  return { libelle: 'En cours', ton: 'accent', jauge: ACCENT, bloquant: false };
}

/**
 * Une ligne « livraison / réception » à mesurer, commune aux trois volets.
 *
 * Trois dispositions, une par palier : quatre colonnes en large, deux en moyen (les trois blocs
 * d'information s'empilent à gauche, le bouton garde sa colonne), tout empilé en compact.
 */
export default function ArrivageCard({ arrivage, onOuvrir, ouvertureEnCours }) {
  const { compact, large } = useBreakpoint();
  const etat = etatArrivage(arrivage);
  const quota = arrivage.quantiteMinRequise || 0;
  const fait = arrivage.nbPiecesMesurees || 0;
  const pct = quota > 0 ? Math.min(Math.round((fait / quota) * 100), 100) : 0;
  const demarre = !arrivage.sessionId;

  const colonnes = large
    ? 'minmax(0, 1.4fr) minmax(0, 1fr) 200px 180px'
    : compact
      ? 'minmax(0, 1fr)'
      : 'minmax(0, 1fr) 180px';

  // En moyen, les trois blocs d'information s'empilent dans la première colonne et le bouton
  // occupe la seconde sur toute la hauteur ; ailleurs, le placement automatique suffit.
  const placement = (indexBloc) => (large || compact ? undefined : { gridColumn: 1, gridRow: indexBloc });
  const placementBouton = large || compact ? undefined : { gridColumn: 2, gridRow: '1 / 4', alignSelf: 'center' };

  return (
    <div
      className="aqs-apparition"
      style={{
        background: SURFACE, borderRadius: RAYON_CARTE, boxShadow: OMBRE_CARTE,
        padding: compact ? 16 : '18px 20px', display: 'grid', gridTemplateColumns: colonnes,
        alignItems: 'center', gap: compact ? 14 : 20,
      }}
    >
      <div style={{ minWidth: 0, ...placement(1) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="aqs-titre" style={{ minWidth: 0, fontSize: 19, fontWeight: 650, letterSpacing: '-0.01em' }}>{arrivage.numeroBon ? `Bon ${arrivage.numeroBon}` : 'Arrivage sans référence'}</span>
          <Capsule ton={etat.ton} point>{etat.libelle}</Capsule>
        </div>
        {!arrivage.normesDefinies && (
          <div className="aqs-para" style={{ marginTop: 6, fontSize: 14, color: AMBRE_FG }}>
            La direction n'a pas encore défini le tableau de mesures de ce modèle.
          </div>
        )}
      </div>
      <div style={{ minWidth: 0, fontSize: 15, color: TEXTE_SECONDAIRE, display: 'flex', flexDirection: compact ? 'row' : 'column', gap: compact ? 18 : 4, flexWrap: 'wrap', ...placement(2) }}>
        <div>Reçu le <span style={{ color: TEXTE, fontWeight: 600 }}>{jourArrivage(arrivage.dateArrivage) || '—'}</span></div>
        <div><span style={{ color: TEXTE, fontWeight: 600 }}>{arrivage.quantite ?? '—'}</span> pièces</div>
      </div>
      <div style={{ minWidth: 0, ...placement(3) }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, fontSize: 14, color: TEXTE_SECONDAIRE }}>
          <span><span style={{ fontSize: 17, fontWeight: 650, color: TEXTE }}>{fait}</span> / {quota || '—'}</span>
          <span style={{ whiteSpace: 'nowrap' }}>mesurées</span>
        </div>
        <div style={{ marginTop: 7, height: 6, borderRadius: 3, background: REMPLISSAGE, overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: 3, background: etat.jauge, width: pct + '%', transition: 'width 600ms var(--aqs-courbe)' }} />
        </div>
        {arrivage.quantiteManquante > 0 && (
          <div className="aqs-para" style={{ marginTop: 5, fontSize: 13, color: TEXTE_MUET }}>Encore {arrivage.quantiteManquante} pour le quota indicatif</div>
        )}
      </div>
      <Bouton
        variante={etat.bloquant ? 'gris' : demarre ? 'plein' : 'teinte'}
        hauteur={52}
        disabled={etat.bloquant || ouvertureEnCours}
        onClick={() => onOuvrir(arrivage)}
        style={{ width: '100%', fontSize: 17, ...placementBouton }}
      >
        {ouvertureEnCours ? 'Ouverture…' : etat.bloquant ? 'Indisponible' : demarre ? 'Commencer' : 'Reprendre'}
      </Bouton>
    </div>
  );
}
